import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useContact } from '../context/ContactContext';
import './DlUs.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
    <path d={d} />
  </svg>
);
const PlaneIcon   = () => <Icon d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />;
const HotelIcon   = () => <Icon d="M3 21V7l9-4 9 4v14M9 21v-8h6v8" />;
const CarIcon     = () => <Icon d="M5 17H3v-5l2-5h14l2 5v5h-2M5 17a2 2 0 104 0M15 17a2 2 0 104 0" />;
const CruiseIcon  = () => <Icon d="M3 17l2-8h14l2 8H3zM12 9V4M8 9V6M16 9V6M3 17c0 2.5 18 2.5 18 0" />;
const WifiIcon    = () => <Icon d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01" />;
const BagIcon     = () => <Icon d="M6 2h12l2 5H4L6 2zM3 7h18v14a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />;
const RefundIcon  = () => <Icon d="M3 12h18M3 12l4-4m-4 4l4 4M21 12l-4-4m4 4l-4 4" />;
const PetIcon     = () => <Icon d="M4.5 9.5a4 4 0 010-5M9 7a4.5 4.5 0 000-4M14 7a4.5 4.5 0 010-4M19.5 9.5a4 4 0 000-5M12 14a4 4 0 100 8 4 4 0 000-8z" />;
const StarIcon    = () => <Icon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const ShieldIcon  = () => <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />;
const GlobeIcon   = () => <Icon d="M12 2a10 10 0 100 20A10 10 0 0012 2zM2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />;
const PhoneIcon   = () => <Icon d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .99h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />;
const CheckIcon   = () => <Icon d="M20 6L9 17l-5-5" />;
const ClockIcon   = () => <Icon d="M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2" />;

// ── Data ─────────────────────────────────────────────────────────────────────
const TOPICS = [
  'Wi-Fi on Board', 'Change Flight', 'Airport Lounge', 'Traveling with Pets',
  'SkyMiles Rewards', 'Baggage Info', 'Refund Policy', 'Seat Upgrades',
  'Check-in Online', 'Special Assistance', 'Travel Advisories', 'Help Center'
];

const DEALS = [
  { city: 'New York', code: 'JFK', price: 139, img: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&q=70' },
  { city: 'Los Angeles', code: 'LAX', price: 169, img: 'https://images.unsplash.com/photo-1534190760961-74e8c1c5c3da?w=400&q=70' },
  { city: 'Atlanta', code: 'ATL', price: 99, img: 'https://images.unsplash.com/photo-1575917649705-5b59aaa12e6b?w=400&q=70' },
  { city: 'Miami', code: 'MIA', price: 189, img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=70' },
  { city: 'Denver', code: 'DEN', price: 109, img: 'https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=400&q=70' },
  { city: 'Seattle', code: 'SEA', price: 149, img: 'https://images.unsplash.com/photo-1502175353174-a7a70e73b362?w=400&q=70' },
  { city: 'Cancún', code: 'CUN', price: 259, img: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=400&q=70' },
  { city: 'London', code: 'LHR', price: 429, img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&q=70' },
];

const SERVICES = [
  { icon: <WifiIcon />, title: 'In-Flight Wi-Fi', desc: 'Stay connected with high-speed Wi-Fi available on most flights.' },
  { icon: <StarIcon />, title: 'SkyMiles Program', desc: 'Earn miles on every trip. Redeem for flights, upgrades & more.' },
  { icon: <BagIcon />, title: 'Baggage Policy', desc: 'Understand what you can bring and fees that may apply.' },
  { icon: <ShieldIcon />, title: 'Travel Protection', desc: 'Protect your trip with flexible travel insurance options.' },
  { icon: <GlobeIcon />, title: 'Global Network', desc: 'Fly to 300+ destinations across 6 continents worldwide.' },
  { icon: <PetIcon />, title: 'Pet Travel', desc: 'Safe, comfortable options for traveling with your furry friends.' },
  { icon: <CheckIcon />, title: 'Easy Check-In', desc: 'Check in online, on mobile, or at our self-service kiosks.' },
  { icon: <ClockIcon />, title: '24/7 Support', desc: 'Our agents are available around the clock to assist you.' },
];

const DESTINATIONS = [
  { city: 'Paris', country: 'France', img: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=800&q=70' },
  { city: 'Tokyo', country: 'Japan', img: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=70' },
  { city: 'Cancún', country: 'Mexico', img: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=400&q=70' },
  { city: 'Rome', country: 'Italy', img: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&q=70' },
  { city: 'Sydney', country: 'Australia', img: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=400&q=70' },
];

const FOOTER_COLS = [
  { title: 'Book Travel', links: ['Book a Flight', 'Book a Hotel', 'Rent a Car', 'Cruise Vacations', 'Flight + Hotel Deals', 'Group Travel'] },
  { title: 'My Trips', links: ['Check In', 'Flight Status', 'View Reservations', 'Change Flight', 'Upgrade Seat', 'Request Refund'] },
  { title: 'Travel Info', links: ['Baggage Info', 'Airport Lounges', 'Wi-Fi Onboard', 'Seat Selection', 'Special Meals', 'Pet Travel'] },
  { title: 'SkyMiles', links: ['Join SkyMiles', 'Earn Miles', 'Redeem Miles', 'Credit Card', 'Partner Offers', 'Medallion Status'] },
  { title: 'About Us', links: ['Our Story', 'Newsroom', 'Investor Relations', 'Sustainability', 'Careers', 'Contact Us'] },
];

// ── Component ─────────────────────────────────────────────────────────────────
const DlUs = () => {
  const { contactSettings } = useContact();
  const navigate = useNavigate();
  const tfn = contactSettings?.tfn || '+1-888-859-0441';
  const tfnClean = tfn.replace(/[^0-9+]/g, '');

  const [activeTab, setActiveTab] = useState('flights');
  const [tripType, setTripType] = useState('roundtrip');
  const [from, setFrom] = useState('');
  const [fromCode, setFromCode] = useState('');
  const [to, setTo] = useState('');
  const [toCode, setToCode] = useState('');
  const [depart, setDepart] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [adults, setAdults] = useState('1');
  const [cabinClass, setCabinClass] = useState('economy');
  const [menuOpen, setMenuOpen] = useState(false);
  const [callPopupOpen, setCallPopupOpen] = useState(false);
  const [callPopupLabel, setCallPopupLabel] = useState('');

  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (fromRef.current && !fromRef.current.contains(e.target)) setFromSuggestions([]);
      if (toRef.current && !toRef.current.contains(e.target)) setToSuggestions([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchAirports = useCallback(async (q, setSuggestions, setLoading) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/flights/airports?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setSuggestions(data.data || []);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  }, []);

  const fromTimer = useRef(null);
  const toTimer   = useRef(null);

  const handleFromChange = (val) => {
    setFrom(val); setFromCode('');
    clearTimeout(fromTimer.current);
    fromTimer.current = setTimeout(() => fetchAirports(val, setFromSuggestions, setFromLoading), 300);
  };
  const handleToChange = (val) => {
    setTo(val); setToCode('');
    clearTimeout(toTimer.current);
    toTimer.current = setTimeout(() => fetchAirports(val, setToSuggestions, setToLoading), 300);
  };
  const selectFrom = (s) => { setFrom(`${s.cityName} (${s.iataCode})`); setFromCode(s.iataCode); setFromSuggestions([]); };
  const selectTo   = (s) => { setTo(`${s.cityName} (${s.iataCode})`);   setToCode(s.iataCode);   setToSuggestions([]); };

  const openCallPopup = (label) => (e) => {
    e.preventDefault();
    setCallPopupLabel(label);
    setCallPopupOpen(true);
    setMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const origin = fromCode || from.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    const dest   = toCode   || to.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    if (!origin || !dest || !depart) return;
    const params = new URLSearchParams({ from: origin, to: dest, departDate: depart, tripType, adults, cabinClass });
    if (tripType === 'roundtrip' && returnDate) params.set('returnDate', returnDate);
    navigate(`/flight-results?${params.toString()}`);
  };

  return (
    <div className="dl-page">
      <Helmet>
        <title>Book Cheap Flights – Lowest Fares Guaranteed | 24/7 Support</title>
        <meta name="description" content="Book flights at the lowest fares. Manage reservations, check in online, earn rewards, and get 24/7 live customer support from certified travel specialists." />
        <meta name="keywords" content="cheap flights, book flights online, airline tickets, flight deals, business class flights, international flights, domestic flights, flight booking, travel deals, SkyMiles" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Book Cheap Flights – Lowest Fares Guaranteed" />
        <meta property="og:url" content="https://mfdholidays.ca/dl/us" />
        <link rel="canonical" href="https://mfdholidays.ca/dl/us" />
      </Helmet>

      <a href="#book" className="dl-skip-link">Skip to Book</a>

      {/* ── Top Bar ── */}
      <div className="dl-topbar">
        <div className="dl-topbar-inner">
          <a href="/help">Help Center</a>
          <span className="dl-topbar-sep">|</span>
          <a href="/flights">Flight Status</a>
          <span className="dl-topbar-sep">|</span>
          <a href={`tel:${tfnClean}`}>Call Us: {tfn}</a>
          <span className="dl-topbar-sep">|</span>
          <span>🇺🇸 EN — USD</span>
        </div>
      </div>

      {/* ── Header ── */}
      <header className="dl-header">
        <div className="dl-header-inner">
          {/* Logo: Red triangle + wordmark */}
          <a href="/" className="dl-logo-wrap">
            <svg className="dl-logo-mark" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="24,4 44,44 4,44" fill="#e31837" />
              <polygon points="24,16 36,40 12,40" fill="#fff" opacity="0.25" />
            </svg>
            <span className="dl-logo-name">Book Flights</span>
          </a>

          <ul className="dl-nav">
            <li><a href="#book" onClick={openCallPopup('Book a Flight')}>Book</a></li>
            <li><a href="#" onClick={openCallPopup('Flight Status')}>Flight Status</a></li>
            <li><a href="#" onClick={openCallPopup('Check-In')}>Check-In</a></li>
            <li><a href="#" onClick={openCallPopup('My Trips')}>My Trips</a></li>
            <li><a href="#" onClick={openCallPopup('SkyMiles')}>SkyMiles</a></li>
          </ul>

          <div className="dl-header-actions">
            <a href={`tel:${tfnClean}`} className="dl-btn-call">📞 {tfn}</a>
            <button className="dl-btn-signin">Sign In</button>
          </div>

          <button className={`dl-hamburger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* ── Mobile Drawer ── */}
      <div className={`dl-mobile-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="dl-mobile-overlay" onClick={() => setMenuOpen(false)} />
        <nav className="dl-mobile-menu">
          <div className="dl-mobile-menu-header">
            <span>Fly Smart Travel</span>
            <button className="dl-mobile-menu-close" onClick={() => setMenuOpen(false)}>✕</button>
          </div>
          <ul className="dl-mobile-nav">
            <li><a href="#" onClick={openCallPopup('Book a Flight')}>✈️&nbsp; Book</a></li>
            <li><a href="#" onClick={openCallPopup('Flight Status')}>🔍&nbsp; Flight Status</a></li>
            <li><a href="#" onClick={openCallPopup('Check-In')}>🧳&nbsp; Check-In</a></li>
            <li><a href="#" onClick={openCallPopup('My Trips')}>📋&nbsp; My Trips</a></li>
            <li><a href="#" onClick={openCallPopup('SkyMiles')}>⭐&nbsp; SkyMiles</a></li>
            <li><a href="/contact" onClick={() => setMenuOpen(false)}>📞&nbsp; Contact Us</a></li>
          </ul>
          <div className="dl-mobile-auth">
            <button className="dl-btn-signin">Sign In</button>
          </div>
        </nav>
      </div>

      {/* ── Promo Banner ── */}
      <div className="dl-promo-banner">
        🎉 <strong>FLASH SALE:</strong> Earn up to 90,000 bonus miles on your first card purchase.&nbsp;
        <a href="/flights">Learn more →</a>
      </div>

      {/* ── Hero + Search ── */}
      <section className="dl-hero" id="book">
        <div className="dl-hero-bg">
          <h1 className="dl-hero-tagline">Keep Climbing. Your Next Adventure Awaits.</h1>
        </div>
        <div className="dl-search-container">
          <div className="dl-search-box">
            {/* Tabs */}
            <div className="dl-search-tabs">
              {[
                { key: 'flights', icon: '✈️', label: 'Flights' },
                { key: 'hotels',  icon: '🏨', label: 'Hotels' },
                { key: 'cars',    icon: '🚗', label: 'Cars' },
                { key: 'cruises', icon: '🚢', label: 'Cruises' },
              ].map(t => (
                <button key={t.key} className={`dl-search-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Flights */}
            {activeTab === 'flights' && (
              <form className="dl-search-form" onSubmit={handleSearch}>
                <div className="dl-trip-type">
                  {[['roundtrip','Round Trip'],['oneway','One Way'],['multi','Multi-City']].map(([v,l]) => (
                    <label key={v}>
                      <input type="radio" name="triptype" value={v} checked={tripType===v} onChange={()=>setTripType(v)} />
                      {l}
                    </label>
                  ))}
                </div>
                <div className="dl-search-row">
                  <div className="dl-field dl-autocomplete-wrap" ref={fromRef}>
                    <label>From</label>
                    <input type="text" placeholder="City or airport" value={from} onChange={e => handleFromChange(e.target.value)} autoComplete="off" required />
                    {fromLoading && <div className="dl-ac-loading">Searching…</div>}
                    {fromSuggestions.length > 0 && (
                      <ul className="dl-ac-dropdown">
                        {fromSuggestions.map(s => (
                          <li key={s.iataCode} onMouseDown={() => selectFrom(s)}>
                            <span className="dl-ac-code">{s.iataCode}</span>
                            <span className="dl-ac-name">{s.cityName}{s.name !== s.cityName ? ` — ${s.name}` : ''}</span>
                            <span className="dl-ac-country">{s.countryName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="dl-field dl-autocomplete-wrap" ref={toRef}>
                    <label>To</label>
                    <input type="text" placeholder="City or airport" value={to} onChange={e => handleToChange(e.target.value)} autoComplete="off" required />
                    {toLoading && <div className="dl-ac-loading">Searching…</div>}
                    {toSuggestions.length > 0 && (
                      <ul className="dl-ac-dropdown">
                        {toSuggestions.map(s => (
                          <li key={s.iataCode} onMouseDown={() => selectTo(s)}>
                            <span className="dl-ac-code">{s.iataCode}</span>
                            <span className="dl-ac-name">{s.cityName}{s.name !== s.cityName ? ` — ${s.name}` : ''}</span>
                            <span className="dl-ac-country">{s.countryName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="dl-field">
                    <label>Depart</label>
                    <input type="date" min={today} value={depart} onChange={e => setDepart(e.target.value)} required />
                  </div>
                  {tripType === 'roundtrip' && (
                    <div className="dl-field">
                      <label>Return</label>
                      <input type="date" min={depart || today} value={returnDate} onChange={e => setReturnDate(e.target.value)} />
                    </div>
                  )}
                  <div className="dl-field">
                    <label>Passengers</label>
                    <select value={adults} onChange={e => setAdults(e.target.value)}>
                      {[1,2,3,4,5,6,7,8,9].map(n => <option key={n} value={n}>{n} Passenger{n>1?'s':''}</option>)}
                    </select>
                  </div>
                  <div className="dl-field">
                    <label>Cabin</label>
                    <select value={cabinClass} onChange={e => setCabinClass(e.target.value)}>
                      <option value="economy">Main Cabin</option>
                      <option value="premium_economy">Comfort+</option>
                      <option value="business">First Class</option>
                      <option value="first">Delta One</option>
                    </select>
                  </div>
                  <button type="submit" className="dl-search-btn">Find Flights</button>
                </div>
              </form>
            )}

            {activeTab === 'hotels' && (
              <div className="dl-search-form">
                <div className="dl-search-row">
                  <div className="dl-field" style={{flex:2}}>
                    <label>Destination</label>
                    <input type="text" placeholder="City, hotel, or landmark" />
                  </div>
                  <div className="dl-field"><label>Check-In</label><input type="date" min={today} /></div>
                  <div className="dl-field"><label>Check-Out</label><input type="date" min={today} /></div>
                  <div className="dl-field">
                    <label>Guests</label>
                    <select><option>1 Guest</option><option>2 Guests</option><option>3 Guests</option></select>
                  </div>
                  <button type="button" className="dl-search-btn">Find Hotels</button>
                </div>
              </div>
            )}

            {activeTab === 'cars' && (
              <div className="dl-search-form">
                <div className="dl-search-row">
                  <div className="dl-field" style={{flex:2}}>
                    <label>Pick-Up Location</label>
                    <input type="text" placeholder="Airport, city, or address" />
                  </div>
                  <div className="dl-field"><label>Pick-Up Date</label><input type="date" min={today} /></div>
                  <div className="dl-field"><label>Return Date</label><input type="date" min={today} /></div>
                  <button type="button" className="dl-search-btn">Find Cars</button>
                </div>
              </div>
            )}

            {activeTab === 'cruises' && (
              <div className="dl-search-form">
                <div className="dl-search-row">
                  <div className="dl-field">
                    <label>Destination</label>
                    <select><option>Caribbean</option><option>Mediterranean</option><option>Alaska</option><option>Bahamas</option><option>Europe</option><option>Asia</option></select>
                  </div>
                  <div className="dl-field">
                    <label>Departure Month</label>
                    <select>{['January','February','March','April','May','June','July','August','September','October','November','December'].map(m=><option key={m}>{m} 2026</option>)}</select>
                  </div>
                  <div className="dl-field">
                    <label>Duration</label>
                    <select><option>Any</option><option>3–5 nights</option><option>6–9 nights</option><option>10+ nights</option></select>
                  </div>
                  <button type="button" className="dl-search-btn">Find Cruises</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Popular Topics ── */}
      <section className="dl-topics">
        <div className="dl-topics-inner">
          <h2 className="dl-section-title">Popular Topics</h2>
          <div className="dl-topics-grid">
            {TOPICS.map(t => <a key={t} href="#" className="dl-topic-chip">{t}</a>)}
          </div>
        </div>
      </section>

      {/* ── Inline Call CTA 1 ── */}
      <div className="dl-cta-bar dl-cta-bar--red">
        <span>✈️ Need help booking your flight? Speak to a live travel expert!</span>
        <a href={`tel:${tfnClean}`} className="dl-cta-bar-btn dl-cta-bar-btn--white">📞 Call Now: {tfn}</a>
      </div>

      {/* ── Flight Deals ── */}
      <section className="dl-deals">
        <div className="dl-deals-inner">
          <div className="dl-deals-header">
            <h2 className="dl-section-title">Popular Destinations</h2>
            <a href="/flights">View all deals →</a>
          </div>
          <div className="dl-cards-grid">
            {DEALS.map(d => (
              <a key={d.code} href={`/flights?to=${d.code}`} className="dl-deal-card">
                <img src={d.img} alt={d.city} className="dl-deal-img" loading="lazy" />
                <div className="dl-deal-info">
                  <div className="dl-deal-city">{d.city}</div>
                  <div className="dl-deal-from">Flights from</div>
                  <div className="dl-deal-price">${d.price} <span>one way</span></div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inline Call CTA 2 ── */}
      <div className="dl-cta-banner">
        <p className="dl-cta-banner-eyebrow">Exclusive Unpublished Fares</p>
        <h3 className="dl-cta-banner-title">Save up to 70% on First Class &amp; Business Tickets</h3>
        <a href={`tel:${tfnClean}`} className="dl-cta-banner-btn">📞 {tfn} — Speak to an Agent</a>
      </div>

      {/* ── Services ── */}
      <section className="dl-services">
        <div className="dl-services-inner">
          <h2 className="dl-section-title">Products &amp; Services</h2>
          <div className="dl-services-grid">
            {SERVICES.map(s => (
              <div key={s.title} className="dl-service-card">
                <div className="dl-service-icon">{s.icon}</div>
                <div className="dl-service-title">{s.title}</div>
                <div className="dl-service-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Loyalty CTA ── */}
      <section className="dl-loyalty">
        <div className="dl-loyalty-inner">
          <h2>Join Our SkyMiles Program</h2>
          <p>Earn miles every time you fly — and on everyday purchases with our co-branded credit card. Redeem for flights, upgrades, and exclusive experiences.</p>
          <div className="dl-loyalty-btns">
            <a href="/flights" className="dl-btn-primary">Enroll Now — It's Free</a>
            <a href="/about" className="dl-btn-outline">Learn About SkyMiles</a>
          </div>
        </div>
      </section>

      {/* ── Destinations Grid ── */}
      <section className="dl-destinations">
        <div className="dl-destinations-inner">
          <h2 className="dl-section-title">Explore the World</h2>
          <div className="dl-dest-grid">
            {DESTINATIONS.map(d => (
              <a key={d.city} href={`/flights?to=${d.city}`} className="dl-dest-tile">
                <img src={d.img} alt={d.city} loading="lazy" />
                <div className="dl-dest-label">
                  {d.city}
                  <span>{d.country}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inline Call CTA 3 ── */}
      <div className="dl-cta-bar dl-cta-bar--urgency">
        <span>🚨 Prices change fast — Lock your fare right now before it's gone!</span>
        <a href={`tel:${tfnClean}`} className="dl-cta-bar-btn dl-cta-bar-btn--red">📞 {tfn}</a>
      </div>

      {/* ── Info Strips ── */}
      <section className="dl-strips">
        <div className="dl-strips-inner">
          <div className="dl-strip-item">
            <div className="dl-strip-icon"><CheckIcon /></div>
            <div className="dl-strip-text"><h4>Flexible Booking</h4><p>Change or cancel most flights without a fee. Travel with confidence.</p></div>
          </div>
          <div className="dl-strip-item">
            <div className="dl-strip-icon"><ShieldIcon /></div>
            <div className="dl-strip-text"><h4>Safe Travel Guarantee</h4><p>Enhanced cleaning, HEPA filtration, and health-first policies on every flight.</p></div>
          </div>
          <div className="dl-strip-item">
            <div className="dl-strip-icon"><GlobeIcon /></div>
            <div className="dl-strip-text"><h4>Global Network</h4><p>Over 300 destinations across 6 continents — wherever life takes you.</p></div>
          </div>
          <div className="dl-strip-item">
            <div className="dl-strip-icon"><PhoneIcon /></div>
            <div className="dl-strip-text"><h4>24/7 Expert Support</h4><p>Live agents available around the clock for all your travel needs.</p></div>
          </div>
        </div>
      </section>

      {/* ── App Promo ── */}
      <section className="dl-app-promo">
        <div className="dl-app-inner">
          <div className="dl-app-text">
            <h2>Download Our App</h2>
            <p>Check in, track your flight in real time, manage your bookings, and access your boarding pass — all from the palm of your hand.</p>
            <div className="dl-app-badges">
              <a href="#" className="dl-app-badge">🍎 App Store</a>
              <a href="#" className="dl-app-badge">▶️ Google Play</a>
            </div>
          </div>
          <div className="dl-app-phone">📱</div>
        </div>
      </section>

      {/* ── Customer Support ── */}
      <section className="dl-support">
        <div className="dl-support-inner">
          <div className="dl-support-header">
            <h2 className="dl-section-title">Need Help? We're Here 24/7</h2>
            <p className="dl-support-subtitle">Our certified travel experts are ready to assist with bookings, changes, refunds, check-in, baggage, and more.</p>
          </div>
          <div className="dl-support-grid">
            {[
              { img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=75', title: 'Flight Booking', desc: 'Book new flights, find best deals, and choose your seats.', label: 'Flight Booking Support' },
              { img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=75', title: 'Manage Reservations', desc: 'Change flights, upgrade seats, add baggage or request a refund.', label: 'Manage My Reservation' },
              { img: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&q=75', title: 'Baggage & Check-In', desc: 'Baggage policies, lost luggage, online check-in, and boarding pass help.', label: 'Baggage & Check-In Help' },
              { img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=75', title: 'SkyMiles Rewards', desc: 'Earn & redeem miles, Medallion status benefits, and partner rewards.', label: 'SkyMiles Rewards' },
            ].map(card => (
              <div key={card.title} className="dl-support-card" onClick={openCallPopup(card.label)}>
                <div className="dl-support-img-wrap">
                  <img src={card.img} alt={card.title} loading="lazy" />
                  <div className="dl-support-overlay"><span className="dl-support-call-badge">📞 Call Now</span></div>
                </div>
                <div className="dl-support-card-body">
                  <h3>{card.title}</h3>
                  <p>{card.desc}</p>
                  <a href={`tel:${tfnClean}`} className="dl-support-btn" onClick={e => e.stopPropagation()}>
                    <span>📞</span> {tfn}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="dl-support-cta">
            <div className="dl-support-cta-icon">📞</div>
            <div className="dl-support-cta-text">
              <strong>Speak with a Travel Expert Now</strong>
              <span>No wait times · Best fares guaranteed · Available 24/7</span>
            </div>
            <a href={`tel:${tfnClean}`} className="dl-support-cta-btn">{tfn}</a>
          </div>
        </div>
      </section>

      {/* ── Call Bar ── */}
      <div className="dl-call-bar">
        <div className="dl-call-bar-inner">
          <div>
            <p>📞 Speak with a travel expert now</p>
            <span>Available 24/7 · Flights, hotels, vacation packages</span>
          </div>
          <a href={`tel:${tfnClean}`}>{tfn}</a>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="dl-footer">
        <div className="dl-footer-inner">
          <div className="dl-footer-grid">
            {FOOTER_COLS.map(col => (
              <div key={col.title} className="dl-footer-col">
                <h4>{col.title}</h4>
                <ul>{col.links.map(link => <li key={link}><a href="#">{link}</a></li>)}</ul>
              </div>
            ))}
          </div>
          <div className="dl-footer-bottom">
            <p>© 2026 Fly Smart Travel. All rights reserved. This is an independent travel booking service.</p>
            <div className="dl-footer-social">
              <a href="#" className="dl-social-btn" aria-label="Facebook">f</a>
              <a href="#" className="dl-social-btn" aria-label="Twitter">𝕏</a>
              <a href="#" className="dl-social-btn" aria-label="Instagram">📷</a>
              <a href="#" className="dl-social-btn" aria-label="LinkedIn">in</a>
            </div>
          </div>
          <div className="dl-footer-links">
            <a href="/terms">Terms &amp; Conditions</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="/help">Help Center</a>
            <a href="/contact">Contact Us</a>
            <a href="#">Accessibility</a>
            <a href="#">Cookie Preferences</a>
          </div>
        </div>
      </footer>

      {/* ── Floating Call Button ── */}
      <a href={`tel:${tfnClean}`} className="dl-float-call" aria-label="Call us now">
        <span className="dl-float-call-icon">📞</span>
        <span className="dl-float-call-text">
          <span className="dl-float-call-label">Call Us 24/7</span>
          <span className="dl-float-call-num">{tfn}</span>
        </span>
      </a>

      {/* ── Call Popup ── */}
      {callPopupOpen && (
        <div className="dl-call-popup-overlay" onClick={() => setCallPopupOpen(false)}>
          <div className="dl-call-popup" onClick={e => e.stopPropagation()}>
            <div className="dl-popup-header">
              <div className="dl-popup-logo">
                <svg viewBox="0 0 36 36" fill="none" width="32" height="32">
                  <polygon points="18,2 34,34 2,34" fill="#e31837" />
                </svg>
                <span>Fly Smart Travel</span>
              </div>
              <button className="dl-call-popup-close" onClick={() => setCallPopupOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="dl-popup-body">
              <div className="dl-popup-badge">Official Travel Support</div>
              <h3 className="dl-call-popup-title">{callPopupLabel}</h3>
              <p className="dl-call-popup-subtitle">
                Speak directly with a <strong>certified travel specialist</strong>. Available <strong>24 hours, 7 days a week</strong> — no automated menus.
              </p>
              <div className="dl-popup-reasons">
                <span>✈️ Book &amp; Change Flights</span>
                <span>🧳 Baggage &amp; Check-In</span>
                <span>⭐ SkyMiles Awards</span>
                <span>📋 Manage Reservation</span>
              </div>
              <a href={`tel:${tfnClean}`} className="dl-call-popup-btn">
                <span className="dl-call-popup-btn-label">📞 &nbsp;Call Now</span>
                <span className="dl-call-popup-btn-number">{tfn}</span>
              </a>
              <div className="dl-popup-trust">
                <span>✔ Skip the hold</span>
                <span>✔ Instant booking</span>
                <span>✔ Free to call</span>
              </div>
              <p className="dl-call-popup-note">This is an independent travel service. All airline names and trademarks belong to their respective owners.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DlUs;
